// Basemap of the console's maps (ADR 0016 decision 21). In the Hub shell the map can use the council's gis tiles
// (a MapLibre style URL); the standalone control-room console serves an offline PMTiles file of Lusaka next to the
// page (URL from console-config.json, never committed to the repository). When neither answers, the maps draw a plain
// coordinate grid, so the incidents, stations and wards (all FER data) still show with every Hub service down.
import type { LayerSpecification, StyleSpecification } from "maplibre-gl";

export interface BasemapConfig {
  /** MapLibre style URL (e.g. the council's gis tiles). */
  styleUrl?: string;
  /** Offline PMTiles archive (vector in the Protomaps schema, or raster) served with the console. */
  pmtilesUrl?: string;
}

export type BasemapKind = "style" | "pmtiles-vector" | "pmtiles-raster" | "grid";

export interface ResolvedBasemap {
  kind: BasemapKind;
  style: StyleSpecification | string;
  /** Words for the legend (never colour alone). */
  label: string;
}

export interface BasemapColours {
  bg: string;
  surface: string;
  border: string;
  muted: string;
  text: string;
}

/** Lusaka district and a margin: the grid and the initial view. */
export const LUSAKA_BBOX: [number, number, number, number] = [28.1, -15.6, 28.5, -15.25];
export const LUSAKA_CENTER: [number, number] = [28.2833, -15.4167];

/** Grid lines every `step` degrees over `bbox` (EPSG:4326), labelled by their coordinate. */
export function gridLines(bbox: [number, number, number, number] = LUSAKA_BBOX, step = 0.05): GeoJSON.FeatureCollection {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const features: GeoJSON.Feature[] = [];
  const round = (x: number) => Math.round(x * 1e6) / 1e6;
  for (let lon = Math.ceil(minLon / step) * step; lon <= maxLon + 1e-9; lon += step) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [[round(lon), minLat], [round(lon), maxLat]] },
      properties: { axis: "lon", value: round(lon) },
    });
  }
  for (let lat = Math.ceil(minLat / step) * step; lat <= maxLat + 1e-9; lat += step) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [[minLon, round(lat)], [maxLon, round(lat)]] },
      properties: { axis: "lat", value: round(lat) },
    });
  }
  return { type: "FeatureCollection", features };
}

/** The plain grid style used when no tiles are available. */
export function gridStyle(c: BasemapColours): StyleSpecification {
  return {
    version: 8,
    sources: { grid: { type: "geojson", data: gridLines() } },
    layers: [
      { id: "background", type: "background", paint: { "background-color": c.bg } },
      { id: "grid", type: "line", source: "grid", paint: { "line-color": c.border, "line-width": 1 } },
    ],
  };
}

/** A minimal light style for a vector PMTiles archive in the Protomaps basemap schema (no labels: no glyphs offline). */
export function pmtilesVectorStyle(url: string, c: BasemapColours): StyleSpecification {
  const src = "basemap";
  const layers: LayerSpecification[] = [
    { id: "background", type: "background", paint: { "background-color": c.bg } },
    { id: "earth", type: "fill", source: src, "source-layer": "earth", paint: { "fill-color": c.bg } },
    { id: "landuse", type: "fill", source: src, "source-layer": "landuse", paint: { "fill-color": c.surface } },
    { id: "water", type: "fill", source: src, "source-layer": "water", paint: { "fill-color": c.border } },
    { id: "buildings", type: "fill", source: src, "source-layer": "buildings", paint: { "fill-color": c.surface, "fill-outline-color": c.border } },
    {
      id: "roads",
      type: "line",
      source: src,
      "source-layer": "roads",
      paint: { "line-color": c.muted, "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 16, 3] },
    },
  ];
  return { version: 8, sources: { [src]: { type: "vector", url: `pmtiles://${url}` } }, layers };
}

export function pmtilesRasterStyle(url: string, c: BasemapColours): StyleSpecification {
  return {
    version: 8,
    sources: { basemap: { type: "raster", url: `pmtiles://${url}`, tileSize: 256 } },
    layers: [
      { id: "background", type: "background", paint: { "background-color": c.bg } },
      { id: "basemap", type: "raster", source: "basemap" },
    ],
  };
}

/** What a PMTiles archive holds, as its header says (`tileType` 1 = MVT vector; 2-5 raster images). */
export interface PmtilesProbe {
  (url: string): Promise<{ tileType: number }>;
}

/** Whether a MapLibre style URL answers (the council's gis tiles may be down). */
export interface StyleProbe {
  (url: string): Promise<boolean>;
}

/**
 * Choose the basemap, in this order (review FW10): the style URL when configured and answering; else the offline
 * PMTiles archive when its header can be read; else (not configured, missing file, unreadable archive) the plain grid.
 * Never throws. Without a style probe a configured style URL is taken as is (the map's own error falls back later).
 */
export async function resolveBasemap(cfg: BasemapConfig, colours: BasemapColours, probe: PmtilesProbe, styleProbe?: StyleProbe): Promise<ResolvedBasemap> {
  if (cfg.styleUrl) {
    let answers = true;
    if (styleProbe) {
      try {
        answers = await styleProbe(cfg.styleUrl);
      } catch {
        answers = false;
      }
    }
    if (answers) return { kind: "style", style: cfg.styleUrl, label: "Council map tiles" };
  }
  if (cfg.pmtilesUrl) {
    try {
      const header = await probe(cfg.pmtilesUrl);
      if (header.tileType === 1) {
        return { kind: "pmtiles-vector", style: pmtilesVectorStyle(cfg.pmtilesUrl, colours), label: "Offline map of Lusaka" };
      }
      if (header.tileType >= 2 && header.tileType <= 5) {
        return { kind: "pmtiles-raster", style: pmtilesRasterStyle(cfg.pmtilesUrl, colours), label: "Offline map of Lusaka" };
      }
    } catch {
      // Missing or unreadable archive: the grid below.
    }
  }
  return {
    kind: "grid",
    style: gridStyle(colours),
    label: cfg.pmtilesUrl || cfg.styleUrl ? "Map tiles unavailable: plain grid (every 0.05°)" : "No map tiles configured: plain grid (every 0.05°)",
  };
}
